import React, { useEffect, useState, useRef } from "react";
import ReusableTable from "../components/ReusableTable";
import GSTExcelExport from "../components/Gstexcelexport";
import { getApiUrl } from "../utils/api";
import { BASE_URL } from "../utils/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useNavigate } from "react-router-dom";

const Ordermanagement = () => {
  const [orders, setOrders] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterDate, setFilterDate] = useState("");
  const invoiceRef = useRef(null);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Range print states
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [rangePrinting, setRangePrinting] = useState(false);

  // ✅ fetchOrders — sends search + dates to backend
  const fetchOrders = async (page, searchTerm, from, to) => {
    const p = page !== undefined ? page : 1;
    const s = searchTerm !== undefined ? searchTerm : search;
    const f = from !== undefined ? from : startDate;
    const t = to !== undefined ? to : endDate;

    try {
      const res = await fetch(getApiUrl(`order-success?page=${p}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          search: s,
          startDate: f,
          endDate: t,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setOrders(data.data);
        setFiltered(data.data);
        setCurrentPage(data.pagination.currentPage);
        setTotalPages(data.pagination.totalPages);
        setTotalOrders(data.pagination.totalOrders);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // ✅ Initial load
  useEffect(() => {
    fetchOrders(1, "", "", "");
  }, []);

  // ✅ Debounce — triggers when search or dates change
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchOrders(1, search, startDate, endDate);
    }, 500);
    return () => clearTimeout(timer);
  }, [search, startDate, endDate]);

  // ✅ Range print — fetches from backend by Si No range
  const handleRangePrint = async (isPdf) => {
    if (!rangeFrom || !rangeTo) {
      return toast.warning("Enter From and To range");
    }
    if (Number(rangeFrom) > Number(rangeTo)) {
      return toast.warning("From must be less than To");
    }

    setRangePrinting(true);
    try {
      const res = await fetch(getApiUrl("order-range"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from: Number(rangeFrom),
          to: Number(rangeTo),
          search,
          startDate,
          endDate,
        }),
      });
      const data = await res.json();
      if (data.success && data.data.length) {
        await printOrDownloadBulk(data.data, isPdf);
      } else {
        toast.error("No orders found in this range");
      }
    } catch (err) {
      toast.error("Failed to fetch range");
    } finally {
      setRangePrinting(false);
    }
  };

  const handleShipChange = async (orderId, status) => {
    try {
      const res = await fetch(getApiUrl(`order/ship-status/${orderId}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shipStatus: status }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Shipping status updated");
        fetchOrders(currentPage, search, startDate, endDate);
      } else {
        toast.error("Failed to update");
      }
    } catch {
      toast.error("Server error");
    }
  };

  const sendWhatsApp = (row) => {
    const phone = row?.userId?.phone;

    if (!phone) {
      alert("Customer phone number not available");
      return;
    }

    const confirmSend = window.confirm(
      `Send shipping message to ${row?.userId?.name || "Customer"}?`
    );
    if (!confirmSend) return;

    const productList = row.products
      .map((p, i) => {
        const name = p.productId?.productName || p.name || "Product";
        return `${i + 1}. ${name} | Size: ${p.size} | Qty: ${p.qty}`;
      })
      .join("\n");

    const message = `
Hello ${row?.userId?.name || "Customer"},

Your order has been shipped successfully.

Order ID: ${row.orderUnique || "N/A"}

Products:
${productList}

Total Amount: ₹${row.amount}

👉🏼 You will get the parcel within 3 to 6 working days

⚠️ Strictly No return / No exchange on quality basis, Size, and colour variation.

⚠️ Must need unboxing video for any damage otherwise we never take return.

🙏🏼 Thanks for dealing with us ❤
`.trim();

    const formattedPhone = phone.replace(/\D/g, "");
    const encodedMessage = encodeURIComponent(message);
    const url = `https://wa.me/${formattedPhone}?text=${encodedMessage}`;
    window.open(url, "_blank");
  };

  const tinyView = {
    padding: "2px 6px",
    fontSize: "11px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    background: "#f5f5f5",
    cursor: "pointer",
    lineHeight: "1.2",
  };

  const tinyWhatsapp = {
    padding: "2px 6px",
    fontSize: "11px",
    borderRadius: "4px",
    border: "none",
    background: "#25D366",
    color: "#fff",
    cursor: "pointer",
    lineHeight: "1.2",
  };

  const columns = [
    {
      name: "Si No",
      selector: (row, index) => totalOrders - ((currentPage - 1) * 10 + index),
      width: "80px",
    },
    {
      name: "Order Ref",
      cell: (row) => (
        <span
          style={{
            background: "#eef2ff",
            color: "#4338ca",
            padding: "2px 6px",
            borderRadius: "4px",
            fontSize: "11px",
            fontWeight: 600,
          }}
        >
          {row.orderReference || "-"}
        </span>
      ),
      width: "140px",
    },
    {
      name: "Product Name",
      cell: (row) => (
        <ul
          className="list-unstyled mb-0"
          style={{ maxHeight: "80px", overflow: "auto" }}
        >
          {row.products.map((p, idx) => (
            <li key={idx} style={{ fontSize: "13px", lineHeight: "1.3" }}>
              {p.productInfo?.productName || p.name || "-"}
            </li>
          ))}
        </ul>
      ),
      width: "180px",
    },
    {
      name: "Subcategory",
      cell: (row) => (
        <ul
          className="list-unstyled mb-0"
          style={{ maxHeight: "60px", overflow: "auto", fontSize: "12px" }}
        >
          {row.products.map((p, idx) => (
            <li key={idx}>{p.productInfo?.subcategory || "-"}</li>
          ))}
        </ul>
      ),
      width: "140px",
    },
    {
      name: "Image",
      cell: (row) => (
        <div
          className="d-flex gap-1"
          style={{
            maxHeight: "70px",
            overflow: "auto",
            flexWrap: "nowrap",
            maxWidth: "100%",
          }}
        >
          {row.products.map((p, idx) => {
            const image = p.productInfo?.mainImage;
            if (!image) return null;
            return (
              <img
                key={idx}
                src={`${BASE_URL}${image}`}
                width="40"
                height="40"
                style={{
                  objectFit: "cover",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                  flexShrink: 0,
                }}
                onError={(e) => (e.target.style.display = "none")}
                alt="Product"
              />
            );
          })}
        </div>
      ),
      width: "160px",
      allowOverflow: true,
    },
    {
      name: "Quantity",
      selector: (row) =>
        row.products.reduce((sum, p) => sum + (p.qty || 0), 0),
      width: "90px",
    },
    {
      name: "Size",
      cell: (row) => (
        <ul
          className="list-unstyled mb-0"
          style={{ fontSize: "12px", maxHeight: "60px", overflow: "auto" }}
        >
          {row.products.map((p, idx) => (
            <li key={idx}>{p.size}</li>
          ))}
        </ul>
      ),
      width: "100px",
    },
    {
      name: "Customer",
      selector: (row) => row?.userId?.name || "Guest",
      width: "140px",
    },
    {
      name: "Amount",
      selector: (row) => `₹${row.amount}`,
      width: "110px",
    },
    {
      name: "Status",
      cell: (row) => {
        let badgeClass = "";
        switch (row.paymentStatus) {
          case "SUCCESS":
            badgeClass = "bg-success";
            break;
          case "PENDING":
            badgeClass = "bg-warning text-dark";
            break;
          case "FAILED":
            badgeClass = "bg-danger";
            break;
          default:
            badgeClass = "bg-secondary";
        }
        return (
          <span className={`badge ${badgeClass}`}>{row.paymentStatus}</span>
        );
      },
      width: "110px",
    },
    {
      name: "Order ID",
      cell: (row) => (
        <span
          style={{
            background: "#dcfce7",
            color: "#166534",
            padding: "2px 6px",
            borderRadius: "4px",
            fontSize: "11px",
            fontWeight: 600,
          }}
        >
          {row.orderUnique || "-"}
        </span>
      ),
      width: "120px",
    },
    {
      name: "Shipping",
      cell: (row) => (
        <select
          className="form-select form-select-sm"
          value={row.shipStatus || "processing"}
          onChange={(e) => handleShipChange(row._id, e.target.value)}
          style={{ fontSize: "12px", padding: "2px 4px" }}
        >
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
        </select>
      ),
      width: "120px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div style={{ display: "flex", gap: "4px", fontSize: "12px" }}>
          <button onClick={() => setSelectedOrder(row)} style={tinyView}>
            View
          </button>
          <button onClick={() => sendWhatsApp(row)} style={tinyWhatsapp}>
            WA
          </button>
        </div>
      ),
      width: "100px",
      right: true,
    },
  ];

  const formatDate = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const monthNames = [
      "Jan","Feb","Mar","Apr","May","Jun",
      "Jul","Aug","Sep","Oct","Nov","Dec",
    ];
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const renderOrderHTML = (order) => `
<style>
* { box-sizing: border-box; font-family: "Segoe UI", Arial, Helvetica, sans-serif; }
body { margin: 0; padding: 0; }
.page { width: 210mm; min-height: 297mm; max-height: 297mm; padding: 15mm; overflow: hidden; display: flex; justify-content: center; background: #fff; }
.container { width: 100%; height: 100%; border: 3px solid #000; padding: 22px 26px; display: flex; flex-direction: column; }
h2 { margin: 0 0 12px 0; font-size: 36px; font-weight: 900; letter-spacing: 1.5px; text-align: center; }
.section { font-size: 20px; border-top: 3px solid #000; padding-top: 10px; margin-bottom: 12px; line-height: 1.5; }
.section-title { font-size: 22px; font-weight: 900; margin-bottom: 6px; text-transform: uppercase; }
table { width: 100%; border-collapse: collapse; font-size: 20px; }
th { border: 2.5px solid #000; padding: 8px; background: #f0f0f0; font-weight: 900; text-align: center; }
td { border: 2px solid #000; padding: 8px; text-align: center; }
td:first-child { text-align: left; }
.total { font-size: 28px; text-align: right; border-top: 4px solid #000; padding-top: 14px; margin-top: auto; font-weight: 900; }
.thank-you { text-align: center; font-size: 20px; font-weight: 800; border-top: 2px solid #000; padding-top: 10px; margin-top: 10px; }
.to-address { border: 4px solid #000; padding: 12px; margin-top: 8px; background: #f9f9f9; }
.to-address .name { font-size: 32px; font-weight: 1000; letter-spacing: 1px; }
</style>
<div class="page print-page">
  <div class="container">
    <div class="logo">
      <img src="${window.location.origin}/image/boutique.jpg" crossorigin="anonymous" />
      <h2>BSK Boutique</h2>
    </div>
    <div class="section">
      <div class="section-title">Order Details</div>
      Order Date: ${formatDate(order.createdAt)}<br/>
    </div>
    <div class="section">
      <strong>FROM:</strong><br/>
      BSK Boutique, Rajapalayam, Tamil Nadu<br/>
      +91 90256 76315<br/><br/>
      <strong>TO:</strong>
      <div class="to-address">
        <div class="name">${order?.userId?.name || "Guest"}</div>
        <div class="name">${order?.address?.address || ""}</div>
        <div class="name">${order?.address?.district || ""}, ${order?.address?.state || ""}</div>
        <div class="name">Pin: ${order?.address?.pincode || "-"}</div>
        <div class="name">Ph: ${order?.userId?.phone || "-"}</div>
      </div>
    </div>
    <div class="section">
      <div class="section-title">Products</div>
      <table>
        <thead>
          <tr><th>Item</th><th>Qty</th><th>Size</th><th>₹</th></tr>
        </thead>
        <tbody>
          ${order.products.map((p) => `
            <tr>
              <td>${p.name}</td>
              <td style="text-align:center;">${p.qty}</td>
              <td style="text-align:center;">${p.size}</td>
              <td>${p.price}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
    <div class="total">TOTAL: ₹${order.amount}</div>
    <div class="thank-you">Thank you for purchasing from BSK Boutique!</div>
  </div>
</div>
`;

  const printOrDownloadBulk = async (ordersList, isPdf) => {
    if (!ordersList.length) return toast.error("No orders found");

    if (!isPdf) {
      const w = window.open("", "_blank");
      w.document.write(`
        <html>
          <head>
            <title>Print Orders</title>
            <style>
              body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; }
              .print-batch { width: 210mm; margin: auto; }
              .print-page { page-break-after: always; }
              @page { size: A4; margin: 0; }
            </style>
          </head>
          <body><div class="print-batch">
      `);
      ordersList.forEach((o) => w.document.write(renderOrderHTML(o)));
      w.document.write(`</div></body></html>`);
      w.document.close();
      setTimeout(() => { w.focus(); w.print(); }, 500);
      return;
    }

    const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4", compress: true });
    for (let i = 0; i < ordersList.length; i++) {
      const div = document.createElement("div");
      div.innerHTML = renderOrderHTML(ordersList[i]);
      div.style.width = "900px";
      div.style.background = "white";
      document.body.appendChild(div);
      await new Promise((r) => setTimeout(r, 300));
      const canvas = await html2canvas(div, { scale: 2, useCORS: true });
      const img = canvas.toDataURL("image/jpeg", 0.8);
      const width = pdf.internal.pageSize.getWidth();
      const height = (canvas.height * width) / canvas.width;
      pdf.addImage(img, "JPEG", 0, 0, width, height);
      document.body.removeChild(div);
      if (i < ordersList.length - 1) pdf.addPage();
    }
    pdf.save(`Orders-${filterDate || "all"}.pdf`);
  };

  const getFilteredOrders = (status) => {
    let list = [...filtered];
    if (status) list = list.filter((o) => o.shipStatus === status);
    return list;
  };




  return (
    <div className="container-fluid mt-4">
      <ToastContainer />

      {/* Back Button */}
      <div className="mb-2">
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "4px 10px",
            fontSize: "12px",
            borderRadius: "6px",
            border: "1px solid #ddd",
            background: "#f5f5f5",
            cursor: "pointer",
          }}
        >
          ← Back
        </button>
      </div>

      {/* Header + Filters */}
      <div className="d-flex justify-content-between flex-wrap gap-2 mb-3">
        <h3 className="fw-bold">Order Management</h3>

        <div className="input-group" style={{ maxWidth: "200px" }}>
          <span className="input-group-text">From</span>
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="input-group" style={{ maxWidth: "200px" }}>
          <span className="input-group-text">To</span>
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div style={{ maxWidth: "250px" }}>
          <input
            type="text"
            className="form-control"
            placeholder="🔍 Search customer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

     {/* Print / PDF Buttons */}
<div className="mb-2 d-flex flex-wrap gap-2">
  <button className="btn btn-primary" onClick={() => printOrDownloadBulk(getFilteredOrders(), false)}>Print All</button>
  <button className="btn btn-outline-primary" onClick={() => printOrDownloadBulk(getFilteredOrders(), true)}>PDF All</button>
  <button className="btn btn-warning" onClick={() => printOrDownloadBulk(getFilteredOrders("processing"), false)}>Print Processing</button>
  <button className="btn btn-outline-warning" onClick={() => printOrDownloadBulk(getFilteredOrders("processing"), true)}>PDF Processing</button>
  <button className="btn btn-success" onClick={() => printOrDownloadBulk(getFilteredOrders("shipped"), false)}>Print Shipped</button>
  <button className="btn btn-outline-success" onClick={() => printOrDownloadBulk(getFilteredOrders("shipped"), true)}>PDF Shipped</button>
</div>  {/* ← close the flex div here, GSTExcelExport goes OUTSIDE */}

{/* GST Excel Export Panel */}
<GSTExcelExport startDate={startDate} endDate={endDate} search={search} />


      
      <div className="card p-3 mb-3 border-warning shadow-sm">
        <div className="d-flex flex-wrap gap-2 align-items-end">
          <div>
            <label className="form-label small fw-bold mb-1">Print From (Si No)</label>
            <input
              type="number"
              className="form-control"
              style={{ width: "100px" }}
              placeholder="1"
              min={1}
              value={rangeFrom}
              onChange={(e) => setRangeFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="form-label small fw-bold mb-1">To (Si No)</label>
            <input
              type="number"
              className="form-control"
              style={{ width: "100px" }}
              placeholder={totalOrders || "243"}
              min={1}
              value={rangeTo}
              onChange={(e) => setRangeTo(e.target.value)}
            />
          </div>
          <button
            className="btn btn-warning"
            onClick={() => handleRangePrint(false)}
            disabled={rangePrinting || !rangeFrom || !rangeTo}
          >
            {rangePrinting ? "Loading..." : "Print Range"}
          </button>
          <button
            className="btn btn-outline-warning"
            onClick={() => handleRangePrint(true)}
            disabled={rangePrinting || !rangeFrom || !rangeTo}
          >
            {rangePrinting ? "Loading..." : "PDF Range"}
          </button>
          {rangeFrom && rangeTo && Number(rangeTo) >= Number(rangeFrom) && (
            <span className="text-muted small align-self-center">
              → {Number(rangeTo) - Number(rangeFrom) + 1} orders will print
            </span>
          )}
          <span className="text-muted small align-self-center">
            (Total: <strong>{totalOrders}</strong> orders)
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <ReusableTable
            columns={columns}
            data={filtered}
            title="Order List"
            currentPage={currentPage}
            totalPages={totalPages}
            totalOrders={totalOrders}
            onChangePage={(page) => fetchOrders(page, search, startDate, endDate)}
          />
        </div>
      </div>

      {/* Order View Modal */}
      {selectedOrder && (
        <div
          className="modal show fade d-block"
          style={{ background: "rgba(0,0,0,.5)" }}
          onClick={() => setSelectedOrder(null)}
        >
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content p-3">
              <div ref={invoiceRef} className="p-4 bg-white print-invoice">
                <div dangerouslySetInnerHTML={{ __html: renderOrderHTML(selectedOrder) }} />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setSelectedOrder(null)}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={() => printOrDownloadBulk([selectedOrder], false)}>
                  Print
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={async () => {
                    await new Promise((r) => setTimeout(r, 300));
                    const canvas = await html2canvas(invoiceRef.current, { scale: 1.6, useCORS: true });
                    const img = canvas.toDataURL("image/jpeg", 0.72);
                    const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4", compress: true });
                    const width = pdf.internal.pageSize.getWidth();
                    const height = (canvas.height * width) / canvas.width;
                    pdf.addImage(img, "JPEG", 0, 0, width, height);
                    pdf.save(`Order-${selectedOrder.merchantOrderId}.pdf`);
                  }}
                >
                  PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ordermanagement;